import { useState } from "react";
import { Button, ButtonGroup, Input } from "reactstrap";
import BooleanIcon from "../common/BooleanIcon";
import { CopyButton, TooltipIcon } from "../common/buttons";
import formatDate from "../common/utils";
import StepBadge from "./StepBadge";
import INSTALLATION_STEPS from "./installationConstants";

const BOOL_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "unset", label: "—" },
  { value: "false", label: "No" },
];

function boolToStr(v) {
  if (v === true) return "true";
  if (v === false) return "false";
  return "unset";
}

function strToBool(s) {
  if (s === "true") return true;
  if (s === "false") return false;
  return null;
}

const ROW_BORDER = "1px solid rgba(255,255,255,0.06)";

export function FieldRow({ label, tooltip, viewContent, editContent, editMode, editable, copyValue }) {
  const showEdit = editable && editMode;
  return (
    <tr>
      <th
        scope="row"
        style={{
          width: "40%",
          fontWeight: "normal",
          paddingLeft: "1rem",
          borderBottom: ROW_BORDER,
          paddingTop: "0.25rem",
          paddingBottom: "0.25rem",
        }}
      >
        <small className="text-muted d-inline-flex align-items-center gap-1">
          {label}
          {tooltip && <TooltipIcon tooltip={tooltip} />}
        </small>
      </th>
      <td style={{ paddingRight: "1rem", borderBottom: ROW_BORDER, paddingTop: "0.25rem", paddingBottom: "0.25rem" }}>
        {showEdit && editContent}
        {!showEdit && (
          <>
            {viewContent ?? <span className="text-muted">—</span>}
            {copyValue && <CopyButton value={copyValue} />}
          </>
        )}
      </td>
    </tr>
  );
}

const SECTION_VARIANTS = {
  warning: {
    container: "border border-warning",
    header: "text-warning",
  },
};

export function Section({ title, children, variant }) {
  const [isOpen, setIsOpen] = useState(true);
  const variantClasses = SECTION_VARIANTS[variant] ?? {};
  return (
    <div
      className={variantClasses.container}
      style={{
        border: variantClasses.container ? undefined : "1px solid rgba(255,255,255,0.1)",
        borderRadius: "6px",
        marginBottom: "1rem",
        overflow: "hidden",
      }}
    >
      <div
        role="button"
        className={variantClasses.header}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 1rem",
          backgroundColor: "rgba(255,255,255,0.05)",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setIsOpen((o) => !o)}
      >
        <strong style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</strong>
        <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`} style={{ fontSize: "0.7rem" }} />
      </div>
      {isOpen && (
        <table className="table table-sm table-borderless mb-0" style={{ tableLayout: "fixed" }}>
          <tbody>{children}</tbody>
        </table>
      )}
    </div>
  );
}

export function useInstallationRows(installation, editMode, editedData, setEditedData) {
  const set = (fieldName) => (value) => setEditedData((prev) => ({ ...prev, [fieldName]: value }));
  const getValue = (fieldName) => (fieldName in editedData ? editedData[fieldName] : installation[fieldName]);

  const textRow = (label, fieldName, { editable = false, copy = false, tooltip } = {}) => {
    const value = getValue(fieldName);
    return (
      <FieldRow
        key={fieldName}
        label={label}
        tooltip={tooltip}
        editable={editable}
        editMode={editMode}
        viewContent={value || null}
        editContent={
          <div style={{ maxWidth: "16rem" }}>
            <Input bsSize="sm" value={value ?? ""} onChange={(e) => set(fieldName)(e.target.value)} />
          </div>
        }
        copyValue={copy && value ? value : undefined}
      />
    );
  };

  const selectRow = (label, fieldName, { options, tooltip } = {}) => {
    const value = getValue(fieldName) ?? "";
    const selected = options.find((option) => option.value === value);
    return (
      <FieldRow
        key={fieldName}
        label={label}
        tooltip={tooltip}
        editable
        editMode={editMode}
        viewContent={selected ? selected.label : value || null}
        editContent={
          <div style={{ maxWidth: "14rem" }}>
            <Input type="select" bsSize="sm" value={value} onChange={(e) => set(fieldName)(e.target.value || null)}>
              {options.map(({ value: optionValue, label: optionLabel }) => (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              ))}
            </Input>
          </div>
        }
      />
    );
  };

  const numberRow = (label, fieldName, { editable = false, copy = false, tooltip } = {}) => {
    const value = getValue(fieldName);
    return (
      <FieldRow
        key={fieldName}
        label={label}
        tooltip={tooltip}
        editable={editable}
        editMode={editMode}
        viewContent={value != null ? String(value) : null}
        editContent={
          <div style={{ maxWidth: "10rem" }}>
            <Input
              bsSize="sm"
              type="number"
              value={value ?? ""}
              onChange={(e) => set(fieldName)(e.target.value === "" ? null : Number(e.target.value))}
            />
          </div>
        }
        copyValue={copy && value != null ? String(value) : undefined}
      />
    );
  };

  const booleanRow = (label, fieldName, { editable = false, copy = false, tooltip, nullable = true } = {}) => {
    const value = getValue(fieldName);
    const options = nullable ? BOOL_OPTIONS : BOOL_OPTIONS.filter((o) => o.value !== "unset");
    return (
      <FieldRow
        key={fieldName}
        label={label}
        tooltip={tooltip}
        editable={editable}
        editMode={editMode}
        viewContent={<BooleanIcon value={value} circle />}
        editContent={
          <ButtonGroup size="sm">
            {options.map(({ value: optionValue, label: optionLabel }) => (
              <Button
                key={optionValue}
                color={boolToStr(value) === optionValue ? "primary" : "secondary"}
                outline={boolToStr(value) !== optionValue}
                onClick={() => set(fieldName)(strToBool(optionValue))}
              >
                {optionLabel}
              </Button>
            ))}
          </ButtonGroup>
        }
        copyValue={copy && value != null ? String(value) : undefined}
      />
    );
  };

  const dateRow = (label, fieldName, { copy = false, tooltip } = {}) => {
    const formatted = installation[fieldName] ? formatDate(installation[fieldName]) : null;
    return (
      <FieldRow
        key={fieldName}
        label={label}
        tooltip={tooltip}
        viewContent={formatted}
        copyValue={copy && formatted ? formatted : undefined}
      />
    );
  };

  const stepRow = (label, { copy = false, tooltip } = {}) => {
    const value = editedData.step ?? installation.step;
    return (
      <FieldRow
        key="step"
        label={label}
        tooltip={tooltip}
        editable
        editMode={editMode}
        viewContent={<StepBadge step={value} />}
        editContent={
          <div style={{ maxWidth: "14rem" }}>
            <Input type="select" bsSize="sm" value={value} onChange={(e) => set("step")(e.target.value)}>
              {Object.entries(INSTALLATION_STEPS).map(([k, { label: l }]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </Input>
          </div>
        }
        copyValue={copy && value ? value : undefined}
      />
    );
  };

  return { textRow, selectRow, numberRow, booleanRow, dateRow, stepRow };
}
